import React from "react";
import { Tag } from "primereact/tag";
import { classNames } from "primereact/utils";
import styles from "./StatusPill.module.scss";
import { StatusType, StatusVariant, StatusStyle } from "./StatusPill.types";

// Import the status configurations, modify as needed for new implementations
import { STATUS_CONFIGS } from "./StatusPill.constants";

const capitalize = (str: string): string => {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1);
};

export interface StatusPillProps {
  status: string;
  statusType: StatusType;
  isSelected?: boolean;
  variant?: StatusVariant;
  className?: string;
  displayText?: string;
  /** data-testid applied to the Tag; omit when rendered per-row/per-item to avoid duplicate test ids */
  testId?: string;
}

export const StatusPill: React.FC<StatusPillProps> = ({
  status,
  statusType,
  isSelected = false,
  variant = "outlined",
  className = "",
  displayText,
  testId,
}) => {
  // Get the status configuration
  const statusConfig = STATUS_CONFIGS[statusType];
  const config: Partial<StatusStyle> = statusConfig?.[status] ?? {};
  // Use backgroundColor if available (for filled pills), otherwise use color
  const colorName = (variant === "filled" && config.backgroundColor) || config.color || "gray";

  // Format the status text
  const formatStatus = (status: string): string => {
    return capitalize(status.replace(/([A-Z])/g, " $1").trim());
  };

  const text = displayText || (status ? formatStatus(status) : "");

  // Combine all classes using CSS modules
  const pillClasses = classNames(
    styles.pill,
    isSelected && styles["pill-selected"],
    styles[`pill-${colorName}`],
    variant === "outlined" && !isSelected && styles.outlined,
    ((variant === "filled" && !isSelected) ||
      (statusType === "user" && (status === "admin" || status === "learner"))) &&
      styles.filled,
    className
  );

  return <Tag className={pillClasses} value={text} data-testid={testId} />;
};
